import { useState, useCallback, useRef } from 'react';
import { useElectron, useUpdateProgress, useUpdateStatus } from './useElectron';
import { environmentConfig } from '../environment';

/**
 * Posibles estados del flujo de actualización
 */
export type UpdateState =
  | 'idle'
  | 'checking'
  | 'up-to-date'
  | 'update-available'
  | 'downloading'
  | 'download-complete'
  | 'installing'
  | 'error';

export interface UseAutoUpdaterResult {
  /** Estado actual del flujo de actualización */
  state: UpdateState;
  /** Porcentaje de progreso de descarga (0-100) */
  progress: number;
  /** Versión disponible para actualizar */
  availableVersion: string | null;
  /** Mensaje de error si el estado es 'error' */
  error: string | null;
  /** true si el auto-updater está disponible (Electron + no MinDef) */
  isAvailable: boolean;
  /** Inicia la búsqueda de actualizaciones y descarga automática si hay */
  checkForUpdates: () => Promise<void>;
  /** Instala la actualización descargada (reinicia la app) */
  installUpdate: () => Promise<void>;
  /** Cancela la descarga en progreso */
  cancelDownload: () => Promise<void>;
  /** Resetea el estado a idle */
  reset: () => void;
}

/**
 * Hook para gestionar el flujo completo de auto-actualización.
 *
 * Usa la API modular de Electron (updates.check/download/install/cancelDownload)
 * con suscripción event-based al progreso via onDownloadProgress.
 *
 * @example
 * ```tsx
 * const { state, progress, availableVersion, checkForUpdates, installUpdate } = useAutoUpdater();
 *
 * // Iniciar búsqueda (también descarga si hay actualización)
 * <button onClick={checkForUpdates} disabled={state === 'checking' || state === 'downloading'}>
 *   Buscar actualizaciones
 * </button>
 *
 * // Mostrar progreso
 * {state === 'downloading' && <p>Descargando: {progress}%</p>}
 *
 * // Instalar cuando termine
 * {state === 'download-complete' && <button onClick={installUpdate}>Instalar</button>}
 * ```
 */
export function useAutoUpdater(): UseAutoUpdaterResult {
  const { updates, isElectron } = useElectron();

  const [state, setState] = useState<UpdateState>('idle');
  const [progress, setProgress] = useState(0);
  const [availableVersion, setAvailableVersion] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Ref para rastrear si el componente está montado
  const mountedRef = useRef(true);

  // Determinar si el updater está disponible
  const isAvailable = isElectron && !!updates && environmentConfig.isAutoUpdateAvailable();

  // Suscribirse al progreso de descarga via events
  useUpdateProgress((progressValue: number) => {
    if (!mountedRef.current) return;

    const roundedProgress = Math.round(progressValue);
    setProgress(roundedProgress);

    // Si el progreso llega al 100%, la descarga se completó
    if (roundedProgress >= 100) {
      setState('download-complete');
    } else if (state !== 'downloading') {
      setState('downloading');
    }
  });

  // Suscribirse a cambios de estado emitidos por el main process
  useUpdateStatus((data) => {
    if (!mountedRef.current) return;

    switch (data.status) {
      case 'downloading-started':
        setState('downloading');
        if (data.version) setAvailableVersion(data.version);
        break;

      case 'download-completed':
        setState('download-complete');
        setProgress(100);
        if (data.version) setAvailableVersion(data.version);
        break;

      case 'download-error':
        setState('error');
        setError(data.error ?? 'Download failed');
        break;

      case 'download-canceled':
        setState('idle');
        setProgress(0);
        setAvailableVersion(null);
        break;

      case 'install-error':
        setState('error');
        setError(data.error ?? 'Installation failed');
        break;
    }
  });

  /**
   * Busca actualizaciones y auto-inicia la descarga si se encuentra una.
   * Flujo: idle → checking → (downloading | up-to-date | error)
   */
  const checkForUpdates = useCallback(async () => {
    if (!updates || state === 'checking' || state === 'downloading') return;

    setState('checking');
    setError(null);
    setProgress(0);
    setAvailableVersion(null);

    try {
      const result = await updates.check();

      if (!result) {
        setState('error');
        setError('No response from update server');
        return;
      }

      // Manejar los distintos status que puede retornar sippar-electron
      // La API moderna retorna { updateAvailable, version, downloadUrl, error, message }
      // El campo 'status' viene del startAutoUpdate de legacy, lo tratamos vía el adapter
      const response = result as any;

      // Si hay un error explícito (e.g. "Not Found")
      if (response.error) {
        if (response.error.includes('Not Found')) {
          setState('up-to-date');
        } else {
          setState('error');
          setError(response.error);
        }
        return;
      }

      // El check de la API moderna retorna updateAvailable
      if (result.updateAvailable && result.version) {
        setAvailableVersion(result.version);
        setState('downloading');
        setProgress(0);

        // Auto-iniciar la descarga
        try {
          await updates.download();
        } catch (downloadError) {
          console.error('[useAutoUpdater] Download failed:', downloadError);
          setState('error');
          setError(downloadError instanceof Error ? downloadError.message : 'Download failed');
        }
        return;
      }

      // Manejar status string de respuestas legacy adaptadas
      if (response.status === 'downloading-started' && response.version) {
        setAvailableVersion(response.version);
        setState('downloading');
        setProgress(0);
        return;
      }

      if (response.status === 'already-updated' || response.status === 'already-downloading') {
        setState('up-to-date');
        return;
      }

      if (response.status === 'no-update-available' || response.status === 'no-download-available') {
        setState('up-to-date');
        return;
      }

      // Default: no hay actualización
      setState('up-to-date');
    } catch (err) {
      console.error('[useAutoUpdater] Check failed:', err);
      setState('error');
      setError(err instanceof Error ? err.message : 'Update check failed');
    }
  }, [updates, state]);

  /**
   * Instala la actualización descargada (reinicia la app)
   */
  const installUpdate = useCallback(async () => {
    if (!updates || state !== 'download-complete') return;

    setState('installing');

    try {
      await updates.install();
      // Si llegamos aquí, la instalación debería reiniciar la app
      // Si no reinicia, volvemos al estado completado
      setState('download-complete');
    } catch (err) {
      console.error('[useAutoUpdater] Install failed:', err);
      setState('error');
      setError(err instanceof Error ? err.message : 'Installation failed');
    }
  }, [updates, state]);

  /**
   * Cancela la descarga en progreso
   */
  const cancelDownload = useCallback(async () => {
    if (!updates || state !== 'downloading') return;

    try {
      await updates.cancelDownload();
      setState('idle');
      setProgress(0);
      setAvailableVersion(null);
    } catch (err) {
      console.error('[useAutoUpdater] Cancel failed:', err);
      // Aún así reseteamos el estado
      setState('idle');
      setProgress(0);
    }
  }, [updates, state]);

  /**
   * Resetea el estado a idle
   */
  const reset = useCallback(() => {
    setState('idle');
    setProgress(0);
    setAvailableVersion(null);
    setError(null);
  }, []);

  return {
    state,
    progress,
    availableVersion,
    error,
    isAvailable,
    checkForUpdates,
    installUpdate,
    cancelDownload,
    reset,
  };
}
